import React from "react";
import { View, Text, TouchableOpacity, Image, StyleSheet } from "react-native";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import LiveStream from "../live-streaming-common-components/LiveStream";
import { AppImages } from "../../utility/AppImages";
import Avatar from "../choose-avatar-components/Avatar";

/**
 * Props interface for SoloStreamingCard component
 */
export interface SoloStreamingCardProps {
  // Video source
  platform: "youtube" | "twitch";
  streamUrl: string;

  // User information
  username: string;
  avatarUrl: string; // Can be avatar name or full URL

  // Stream metadata
  title: string;
  viewerCount: number | string; // e.g., "1.2k" or 1200
  isLive?: boolean;

  // Optional styling
  width?: number | string;
  containerStyle?: any;

  // Callbacks
  onPress?: () => void;
  onAvatarPress?: () => void;
}

/**
 * SoloStreamingCard - Main card component for solo streaming feature
 * Displays streaming video with live badge, viewer count, title, and user info
 */
const SoloStreamingCard = ({
  platform,
  streamUrl,
  username,
  avatarUrl,
  title,
  viewerCount,
  isLive = true,
  width = 356,
  containerStyle,
  onPress,
}: SoloStreamingCardProps) => {
  const deviceSize = useDeviceSize();

  const isSmallDevice =
    deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

  // Format viewer count (handles both string and number)
  const formatViewerCount = (count: number | string): string => {
    if (typeof count === "string") return count;

    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  const cardWidth = typeof width === "number" ? width : "100%";
  const videoHeight = isSmallDevice ? 200 : 280;

  return (
    <TouchableOpacity
      style={[styles.cardContainer, { width: cardWidth }, containerStyle]}
      onPress={onPress}
      activeOpacity={0.9}
    >
      {/* Video Player Section */}
      <View style={styles.videoContainer} pointerEvents="none">
        <LiveStream
          platform={platform}
          streamId={streamUrl}
          width="100%"
          height={videoHeight}
          style={{ borderRadius: 12, overflow: "hidden" }}
          hideControls={true}
          preventRedirect={true}
        />

        {/* Live Badge - Top Left */}
        {isLive && (
          <View style={styles.liveBadge}>
            <Text style={styles.liveBadgeText}>Live</Text>
          </View>
        )}

        {/* Viewer Count - Top Right */}
        <View style={styles.viewerBadge}>
          <Image source={AppImages.icWhiteEye} style={styles.eyeIcon} />
          <Text style={styles.viewerCount}>
            {formatViewerCount(viewerCount)}
          </Text>
        </View>
      </View>

      {/* Stream Info Section */}
      <View style={styles.infoContainer}>
        {/* Title */}
        <Text style={styles.streamTitle} numberOfLines={2}>
          {title}
        </Text>

        {/* User Info */}
        {/* User Info */}
        <View style={styles.userInfo}>
          <Avatar src={avatarUrl} variant="profile" />
          <Text style={styles.username}>@{username}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    backgroundColor: "#1E1E2E",
    borderRadius: 16,
    overflow: "hidden",
    marginBottom: 16,
  },
  videoContainer: {
    position: "relative",
    width: "100%",
  },
  liveBadge: {
    position: "absolute",
    top: 12,
    left: 12,
    backgroundColor: "#DC2626",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    zIndex: 10,
  },
  liveBadgeText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "700",
    textTransform: "uppercase",
  },
  liveBadgeTextSmall: {
    fontSize: 12,
  },
  viewerBadge: {
    position: "absolute",
    top: 12,
    right: 12,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 20,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    zIndex: 10,
  },
  eyeIcon: {
    width: 16,
    height: 16,
    tintColor: "#FFFFFF",
  },
  eyeIconSmall: {
    width: 14,
    height: 14,
  },
  viewerCount: {
    color: "#FFFFFF",
    fontSize: 13,
    fontWeight: "600",
  },
  viewerCountSmall: {
    fontSize: 11,
  },
  infoContainer: {
    padding: 16,
    gap: 12,
  },
  infoContainerSmall: {
    padding: 12,
  },
  streamTitle: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "700",
    lineHeight: 24,
  },
  streamTitleSmall: {
    fontSize: 15,
    lineHeight: 20,
  },
  userInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  username: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "600",
  },
  usernameSmall: {
    fontSize: 13,
  },
});

export default SoloStreamingCard;
